import { faExclamationCircle, faEye, faEyeSlash, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import Card from '@sharedComponents/Card';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import InputText from '@sharedComponents/InputText';
import { List, ListItem } from '@sharedComponents/List';
import Typography from '@sharedComponents/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ResetConfirmation({ email, onSave }) {
    const { t } = useTranslation();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState(null);
    const [passwordCriteria, setPasswordCriteria] = useState({
        length: 'neutral',
        uppercase: 'neutral',
        lowercase: 'neutral',
        number: 'neutral',
        specialChar: 'neutral',
        passwordsMatch: 'neutral',
    });

    const handleNewPasswordChange = (e) => {
        setError(null);
        const v = e.target.value;
        setNewPassword(v);
        setPasswordCriteria({
            length: v === '' ? 'neutral' : v.length >= 12 ? 'success' : 'fail',
            uppercase: v === '' ? 'neutral' : /[A-Z]/.test(v) ? 'success' : 'fail',
            lowercase: v === '' ? 'neutral' : /[a-z]/.test(v) ? 'success' : 'fail',
            number: v === '' ? 'neutral' : /[0-9]/.test(v) ? 'success' : 'fail',
            specialChar: v === '' ? 'neutral' : /[!@#$%^&*(),.?":{}|<>]/.test(v) ? 'success' : 'fail',
            passwordsMatch: v === '' ? 'neutral' : v === confirmPassword ? 'success' : 'fail',
        });
    };

    const handleConfirmPasswordChange = (e) => {
        setError(null);
        const v = e.target.value;
        setConfirmPassword(v);
        setPasswordCriteria((prev) => ({
            ...prev,
            passwordsMatch: v === '' ? 'neutral' : v === newPassword ? 'success' : 'fail',
        }));
    };

    const handleSave = async () => {
        try {
            await onSave(newPassword);
        } catch {
            setError(t('An error occurred while saving your password. Please try again.'));
        }
    };

    const allCriteriaMet = Object.values(passwordCriteria).every((val) => val === 'success');

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                if (!allCriteriaMet) return;
                handleSave();
            }}
        >
            <button type="submit" hidden />
            <FlexBox flexDirection="column" gap="l">
                <Typography color="neutral-primary" fontSize="xl" fontWeight="semi-bold">
                    {t('Reset your password')}
                </Typography>
                <Typography fontSize="l">{t('Choose a new password for your account')}</Typography>
                <Card theme="login">
                    <FlexBox flexDirection="column" gap="s">
                        <Typography color="disabled">{t('Username')}</Typography>
                        <InputText placeholder={t('company.name')} name="email" autoComplete="username" value={email} disabled />
                        <Typography>{t('New password')}</Typography>
                        <InputText
                            placeholder={t('New password')}
                            name="newPassword"
                            autoComplete="new-password"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={handleNewPasswordChange}
                        />
                        <FlexBox justifyContent="flex-end">
                            <Button
                                label={showNewPassword ? t('Hide') : t('Show')}
                                action={() => setShowNewPassword((v) => !v)}
                                ghost
                                icon={showNewPassword ? faEyeSlash : faEye}
                                disabled={!newPassword}
                            />
                        </FlexBox>
                        <Typography>{t('Confirm new password')}</Typography>
                        <InputText
                            placeholder={t('Confirm new password')}
                            name="confirmPassword"
                            autoComplete="new-password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                        />
                        <FlexBox justifyContent="flex-end">
                            <Button
                                label={showConfirmPassword ? t('Hide') : t('Show')}
                                action={() => setShowConfirmPassword((v) => !v)}
                                ghost
                                icon={showConfirmPassword ? faEyeSlash : faEye}
                                disabled={!confirmPassword}
                            />
                        </FlexBox>
                    </FlexBox>
                </Card>
                <List title={t('Meet the following criteria:')}>
                    <ListItem status={passwordCriteria.length} customStyle={passwordCriteria.length === 'fail' ? 'textFailColor' : null}>
                        <Typography>{t('At least 12 characters')}</Typography>
                    </ListItem>
                    <ListItem
                        status={passwordCriteria.uppercase}
                        customStyle={passwordCriteria.uppercase === 'fail' ? 'textFailColor' : null}
                    >
                        <Typography>{t('At least 1 uppercase letter')}</Typography>
                    </ListItem>
                    <ListItem
                        status={passwordCriteria.lowercase}
                        customStyle={passwordCriteria.lowercase === 'fail' ? 'textFailColor' : null}
                    >
                        <Typography>{t('At least 1 lowercase letter')}</Typography>
                    </ListItem>
                    <ListItem status={passwordCriteria.number} customStyle={passwordCriteria.number === 'fail' ? 'textFailColor' : null}>
                        <Typography>{t('At least 1 number')}</Typography>
                    </ListItem>
                    <ListItem
                        status={passwordCriteria.specialChar}
                        customStyle={passwordCriteria.specialChar === 'fail' ? 'textFailColor' : null}
                    >
                        <Typography>{t('At least 1 special character')}</Typography>
                    </ListItem>
                    <ListItem
                        status={passwordCriteria.passwordsMatch}
                        customStyle={passwordCriteria.passwordsMatch === 'fail' ? 'textFailColor' : null}
                    >
                        <Typography>{t('Both passwords are identical')}</Typography>
                    </ListItem>
                </List>
                {error && <InformationMessage status="error" text={error} icon={faExclamationCircle} />}
                <FlexBox flexDirection="column" justifyContent="center" gap="m" alignItems="center">
                    <Button label={t('Save the new password')} action={handleSave} disabled={!allCriteriaMet} />
                    <Button
                        label={t('Help')}
                        action="https://support-tests.zendesk.com/hc/fr/categories/360001329772"
                        ghost
                        icon={faQuestionCircle}
                    />
                </FlexBox>
            </FlexBox>
        </form>
    );
}
